import React from 'react';
import { CVData } from '../../types';
import { BaseTemplate } from './BaseTemplate';
import { generateTemplateColors } from '../../lib/colorUtils';

interface Template4Props {
  data: CVData;
  pageNumber?: number;
  customColor?: string;
}

// Función para acortar y optimizar las habilidades
const optimizeSkills = (skills: string[] | undefined) => {
  if (!skills || skills.length === 0) return [];
  
  // Reducimos a 4 habilidades máximo para no entorpecer con la sección de idiomas
  if (skills.length > 4) {
    return skills
      .map(skill => {
        // Acortamos aún más las habilidades largas (máximo 25 caracteres)
        if (skill.length > 25) {
          // Buscar el primer punto, coma o 'con' para cortar
          const endPoints = [
            skill.indexOf('.'),
            skill.indexOf(','),
            skill.indexOf(' con '),
            skill.indexOf(' y ')
          ].filter(i => i > 0);
          
          const endIndex = endPoints.length > 0 ? 
            Math.min(...endPoints) : 
            25;
          
          return skill.substring(0, endIndex > 0 ? endIndex : 25).trim();
        }
        return skill;
      })
      .slice(0, 4); // Limitamos a 4 habilidades
  }
  return skills;
};

export function Template4({ data, pageNumber = 1, customColor }: Template4Props) {
  // Usar el color personalizado si está disponible, o el color por defecto
  const baseColor = customColor || '#DB7093';
  
  console.log("Template4 - Color personalizado recibido:", customColor);
  console.log("Template4 - Color base utilizado:", baseColor);
  
  // Generar colores derivados basados en el color principal
  const colors = generateTemplateColors(baseColor);
  
  console.log("Template4 - Colores generados:", colors);

  // Optimizamos las habilidades si existen
  const optimizedSkills = optimizeSkills(data.skills);
  
  // Creamos una versión optimizada de los datos
  const optimizedData = {
    ...data,
    skills: optimizedSkills
  };

  // Verificamos si hay cursos para mostrar
  const hasCourses = data.courses && data.courses.length > 0;
  
  // Verificamos si es una página de continuación
  const isContinuationPage = (data as any).isContinuationPage || false;
  
  // Verificamos si es una página de continuación específica del caso 3
  const isCase3ContinuationPage = (data as any).isCase3ContinuationPage || false;
  
  // Verificamos si es una página de continuación específica del caso 4
  const isCase4ContinuationPage = (data as any).isCase4ContinuationPage || false;
  
  // Verificamos si es la segunda página del caso 5 (3 experiencias)
  const isCase5SecondPage = (data as any).isCase5SecondPage || false;
  
  // Verificamos si es la tercera página del caso 5 (solo cursos)
  const isCase5ThirdPage = (data as any).isCase5ThirdPage || false;
  
  // Verificamos si es la segunda o tercera página del caso 6
  const isCase6SecondPage = (data as any).isCase6SecondPage || false;
  const isCase6ThirdPage = (data as any).isCase6ThirdPage || false;
  
  // Verificamos si es la segunda página del caso 7
  const isCase7SecondPage = (data as any).isCase7SecondPage || false;
  
  // Verificamos si es la tercera página del caso 7
  const isCase7ThirdPage = (data as any).isCase7ThirdPage || false;
  
  // Verificamos si es la segunda, tercera o cuarta página del caso 8
  const isCase8SecondPage = (data as any).isCase8SecondPage || false;
  const isCase8ThirdPage = (data as any).isCase8ThirdPage || false;
  const isCase8FourthPage = (data as any).isCase8FourthPage || false;
  
  // Verificamos si es una página de continuación con experiencia + cursos
  const isExperiencePlusCourses = (data as any).isExperiencePlusCourses || false;
  
  // Verificamos si es una página de continuación que incluye experiencias
  const isContinuationWithExperiences = (data as any).isContinuationWithExperiences || false;
  
  // Verificamos si es una página de sólo cursos (caso 2, caso 5 tercera página o caso 8 cuarta página)
  const isOnlyCoursesPage = isCase5ThirdPage || isCase8FourthPage || (data as any).isOnlyCoursesPage || false;

  // Verificamos si es una página de continuación para cualquier caso
  const isAnyCaseContinuationPage = isCase3ContinuationPage || isCase4ContinuationPage || 
                                   isCase5SecondPage || isCase5ThirdPage || 
                                   isCase6SecondPage || isCase6ThirdPage || 
                                   isCase7SecondPage || isCase7ThirdPage ||
                                   isCase8SecondPage || isCase8ThirdPage || isCase8FourthPage;

  console.log("Template4 - Es continuación:", isContinuationPage);
  console.log("Template4 - Hay cursos:", hasCourses);
  console.log("Template4 - Longitud de experiencias:", data.experience?.length || 0);
  console.log("Template4 - Longitud de cursos:", data.courses?.length || 0);

  // Definimos el estilo de borde personalizado
  const borderStyle = `border border-[${baseColor}]/30 rounded-2xl`;

  // Función para renderizar los cursos
  const renderCourses = () => {
    if (!data.courses || data.courses.length === 0) {
      console.log("Template4 - No hay cursos para renderizar");
      return null;
    }
    
    console.log("Template4 - Renderizando cursos:", data.courses.length);
    return (
      <div className={`p-5 bg-white/80 backdrop-blur-sm ${borderStyle} shadow-lg mb-5`}>
        <h3 className="text-xl font-bold mb-4" style={{ color: colors.accentColor }}>Cursos y Actividades Adicionales</h3>
        <div className="relative">
          {/* Línea vertical que conecta todos los elementos */}
          <div 
            className="absolute left-2 top-0 bottom-0 w-0.5" 
            style={{ backgroundColor: `${colors.accentColor}40` }}
          ></div>
          
          {/* Contenedor para todos los cursos */}
          <div className="space-y-4 ml-10">
            {data.courses.map((course, index) => (
              <div key={`course-${index}`} className="relative">
                {/* Punto en la línea de tiempo */}
                <div 
                  className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                  style={{ 
                    backgroundColor: 'white', 
                    borderColor: colors.accentColor 
                  }}
                ></div>
                
                {/* Contenido del curso */}
                <div>
                  <h4 className="font-bold text-gray-700">{course.title}</h4>
                  <div className="flex items-center">
                    <p className="text-sm" style={{ color: colors.accentColor }}>{course.institution}</p>
                    <span className="text-sm mx-2" style={{ color: colors.accentColor }}>|</span>
                    <p className="text-sm" style={{ color: colors.accentColor }}>{course.period}</p>
                  </div>
                  {course.description && (
                    <p className="text-sm text-gray-600 mt-1">{course.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Determinar si esta página debe mostrar el título de continuación de experiencia
  const shouldShowExperienceContinuationTitle = 
    isContinuationPage && data.experience?.length > 0 && 
    (isCase3ContinuationPage || isCase4ContinuationPage || isCase5SecondPage || 
     isCase6SecondPage || isCase6ThirdPage || isCase7SecondPage || isCase7ThirdPage ||
     isCase8SecondPage || isCase8ThirdPage);
  
  // Determinar si esta página debe mostrar cursos
  const shouldShowCourses = hasCourses && 
    (isOnlyCoursesPage || isCase5ThirdPage || isCase6ThirdPage || isCase7ThirdPage || isCase8FourthPage || 
    (isContinuationPage && !isCase5SecondPage && !isCase6SecondPage && !isCase7SecondPage && 
     !isCase8SecondPage && !isCase8ThirdPage));

  // Si es una página solo de cursos, solo mostramos los cursos
  if (isOnlyCoursesPage && hasCourses) {
    console.log("Template4 - Renderizando SOLO cursos sin otro contenido");
    return (
      <BaseTemplate
        data={optimizedData}
        colors={colors}
        isTemplate1={false}
        darkMode={false}
        renderMainContent={() => (
          <div className="py-5">
            {renderCourses()}
          </div>
        )}
      />
    );
  }
  
  // Si es una página de continuación con experiencias
  if (isAnyCaseContinuationPage && data.experience && data.experience.length > 0) {
    console.log("Template4 - Renderizando continuación con experiencia");
    
    // Para los casos específicos, determinar si se deben mostrar cursos
    const shouldShowCoursesInContinuation = 
      hasCourses && 
      (isCase3ContinuationPage || isCase4ContinuationPage || isCase6ThirdPage || isCase7ThirdPage) && 
      !(isCase5SecondPage || isCase6SecondPage || isCase7SecondPage || 
        isCase8SecondPage || isCase8ThirdPage);
    
    return (
      <BaseTemplate
        data={optimizedData}
        colors={colors}
        isTemplate1={false}
        darkMode={false}
        renderMainContent={(experienceItems) => (
          <div className="space-y-5">
            {/* Experiencia laboral (continuación) */}
            {experienceItems && React.Children.count(experienceItems) > 0 && (
              <div className={`p-5 bg-white/80 backdrop-blur-sm ${borderStyle} shadow-lg mb-5`}>
                <h3 className="text-xl font-bold mb-4" style={{ color: colors.accentColor }}>
                  Experiencia laboral (continuación)
                </h3>
                <div className="relative">
                  {/* Línea vertical que conecta todos los elementos */}
                  <div 
                    className="absolute left-2 top-0 bottom-0 w-0.5" 
                    style={{ backgroundColor: `${colors.accentColor}40` }}
                  ></div>
                  
                  {/* Contenedor para todas las experiencias */}
                  <div className="space-y-6 ml-10">
                    {React.Children.map(experienceItems, (item, index) => (
                      <div key={`exp-case-${index}`} className="relative">
                        {/* Punto en la línea de tiempo */}
                        <div 
                          className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                          style={{ 
                            backgroundColor: 'white', 
                            borderColor: colors.accentColor 
                          }}
                        ></div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Cursos y Actividades (para casos específicos) */}
            {shouldShowCoursesInContinuation && renderCourses()}
          </div>
        )}
      />
    );
  }

  return (
    <BaseTemplate
      data={optimizedData}
      colors={colors}
      isTemplate1={false}
      darkMode={false}
      renderMainContent={(experienceItems, pageNumber) => (
        <>
          {pageNumber === 1 ? (
            <>
              {/* Encabezado */}
              <div className={`p-5 bg-white/80 backdrop-blur-sm ${borderStyle} shadow-lg mb-5`}>
                <h1 className="text-3xl font-bold mb-1" style={{ color: colors.accentColor }}>{data.name}</h1>
                <h2 className="text-xl font-bold mb-2" style={{ color: colors.accentColor }}>{data.title}</h2>
              </div>

              {/* Educación */}
              {data.education && data.education.length > 0 && (
                <div className={`p-5 bg-white/80 backdrop-blur-sm ${borderStyle} shadow-lg mb-5`}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: colors.accentColor }}>Educación</h3>
                  <div className="relative">
                    {/* Línea vertical que conecta todos los elementos */}
                    <div 
                      className="absolute left-2 top-0 bottom-0 w-0.5" 
                      style={{ backgroundColor: `${colors.accentColor}40` }}
                    ></div>
                    
                    {/* Contenedor para todos los elementos de educación */}
                    <div className="space-y-3 ml-10">
                      {data.education.map((edu, index) => (
                        <div key={`edu-${index}`} className="relative">
                          {/* Punto en la línea de tiempo */}
                          <div 
                            className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                            style={{ 
                              backgroundColor: 'white', 
                              borderColor: colors.accentColor 
                            }}
                          ></div>
                          
                          {/* Contenido de educación */}
                          <div>
                            <h4 className="font-bold text-gray-700">{edu.degree}</h4>
                            <p className="text-sm" style={{ color: colors.accentColor }}>{edu.institution}</p>
                            <p className="text-xs text-gray-500">{edu.period}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Experiencia */}
              <div className={`p-5 bg-white/80 backdrop-blur-sm ${borderStyle} shadow-lg`}>
                <h3 className="text-xl font-bold mb-4" style={{ color: colors.accentColor }}>Experiencia laboral</h3>
                <div className="relative">
                  {/* Línea vertical que conecta todos los elementos */}
                  <div 
                    className="absolute left-2 top-0 bottom-0 w-0.5" 
                    style={{ backgroundColor: `${colors.accentColor}40` }}
                  ></div>
                  
                  {/* Contenedor para todas las experiencias */}
                  <div className="space-y-6 ml-10">
                    {React.Children.map(experienceItems, (item, index) => (
                      <div key={`exp-${index}`} className="relative">
                        {/* Punto en la línea de tiempo */}
                        <div 
                          className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                          style={{ 
                            backgroundColor: 'white', 
                            borderColor: colors.accentColor 
                          }}
                        ></div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Si es otra página, mostramos contenido alternativo según el caso */}
              {/* Experiencia laboral (continuación) */}
              {experienceItems && React.Children.count(experienceItems) > 0 && (
                <div className={`p-5 bg-white/80 backdrop-blur-sm ${borderStyle} shadow-lg mb-5`}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: colors.accentColor }}>
                    Experiencia laboral (continuación)
                  </h3>
                  <div className="relative">
                    {/* Línea vertical que conecta todos los elementos */}
                    <div 
                      className="absolute left-2 top-0 bottom-0 w-0.5" 
                      style={{ backgroundColor: `${colors.accentColor}40` }}
                    ></div>
                    
                    {/* Contenedor para todas las experiencias */}
                    <div className="space-y-6 ml-10">
                      {React.Children.map(experienceItems, (item, index) => (
                        <div key={`exp-cont-${index}`} className="relative">
                          {/* Punto en la línea de tiempo */}
                          <div 
                            className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                            style={{ 
                              backgroundColor: 'white', 
                              borderColor: colors.accentColor 
                            }}
                          ></div>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Cursos y Actividades Adicionales */}
              {hasCourses && !isCase5SecondPage && !isCase6SecondPage && !isCase7SecondPage && 
               !isCase8SecondPage && !isCase8ThirdPage && renderCourses()}
            </>
          )}
        </>
      )}
    />
  );
}